/**
 * @file Locations.js
 * @description This file contains the Locations component, which is reusable for displaying and editing an agency's location information.
 */

import { useState, useEffect, useContext, useMemo } from 'react';
import '../page-list/PageList.css';
import Toast from '../ui/toasts/Toast';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { FilterModal, useFilter } from '../helpers/FilterComponent';
import {
    fetchAgencyOffices,
    updateAgencyOffice,
    deleteAgencyOffice,
} from '../../services/systemService';
import AlertBox from '../ui/no_data_indicators/AlertBox';
import LocationsForm from './LocationsForm';
import { LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { useAuthContext } from '../../store/AuthContext';
import { SecurityAttributes } from '../../utils/data/enums';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const LocationsTab = ({ agencyId }) => {
    const { showModal } = useContext(ModalContext);
    const [isUnauthorized, setUnauthorized] = useState(false);
    const [showList, setShowList] = useState(true);
    const [currentName, setCurrentName] = useState();
    const [currentCode, setCurrentCode] = useState();
    const [currentIsActive, setCurrentIsActive] = useState();
    const [currentLocationId, setCurrentLocationId] = useState();
    const { hasAttribute } = useAuthContext();
    const canEditForm = hasAttribute(SecurityAttributes.AgencyFullAccess);

    useEffect(() => {
        if (agencyId) {
            loadAgencyLocations();
        }
    }, [agencyId]);

    const [agencyLocations, setAgencyLocations] = useState([]);
    const [rowsClicked, setRowsClicked] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const [filteredData, applyFilter] = useFilter(agencyLocations);
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});

    const openModal = column => {
        setActiveModal(column);
    };
    const closeModal = () => {
        setActiveModal(null);
    };
    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };
    // Compute available options considering active filters for current modal
    const getOptions = column => {
        // Remove the filter effect of the current column from the selections state
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column]; // Remove the current column's filter

        // Use these modified filters to filter data
        const filteredData = agencyLocations.filter(agencyLocation =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(agencyLocation[key]),
            ),
        );

        // Now get all unique values for the current column from this filtered dataset
        return Array.from(new Set(filteredData.map(item => item[column])));
    };

    const handleAdd = () => {
        //show add/edit
        setCurrentName('');
        setCurrentCode('');
        setCurrentIsActive(true);
        setShowList(false);
    };

    const handleEdit = (locationId, name, code, isActive) => {
        //show add/edit
        setCurrentName(name);
        setCurrentCode(code);
        setCurrentIsActive(isActive);
        setCurrentLocationId(locationId);
        setShowList(false);
    };

    const handleSave = () => {
        //hide add/edit
        loadAgencyLocations();
        setShowList(true);
    };

    useEffect(() => {
        loadAgencyLocations();
    }, []);

    const loadAgencyLocations = async () => {
        try {
            const response = await fetchAgencyOffices(agencyId);

            setAgencyLocations(response);
        } catch (error) {
            console.error('Failed to fetch agency locations', error);
            Toast.error(getText('msg_afb0d6afb4f1'));

            if (error?.status === 400) {
                setUnauthorized(true);
            }
        }
    };

    const sortedAgencyLocations = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aValue;
            let bValue;

            aValue = String(a[sortConfig.key]);
            bValue = String(b[sortConfig.key]);

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();
            if (aValue < bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? -1
                    : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? 1
                    : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    let handleSortName = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'name',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'name',
                direction: SortDirections.ASCENDING,
            });
        }
    };
    let handleSortCode = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'code',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'code',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    const handlerRowClicked = agencyLocation => {
        let newRowsClicked = rowsClicked;
        if (!rowsClicked.includes(agencyLocation)) {
            newRowsClicked.push(agencyLocation);
        } else {
            newRowsClicked.splice(newRowsClicked.indexOf(agencyLocation), 1);
        }
        setRowsClicked([...newRowsClicked]);
    };

    const deleteAgencyLocations = () => {
        rowsClicked.map(row => {
            agencyLocations.filter(agencyLocation => {
                if (agencyLocation.id === row.id) {
                    deleteAgencyOffice(agencyLocation.id)
                        .then(() => {
                            Toast.success(
                                "'" +
                                agencyLocation.name +
                                "' was deleted successfully.",
                            );
                            deselectRows(rowsClicked);
                            loadAgencyLocations();
                        })
                        .catch(() => {
                            Toast.error(
                                getText('msg_6cd119c7c3bb'),
                            );
                        });
                }
            });
        });
    };

    const deselectRows = rowsClicked => {
        rowsClicked.map(row => {
            agencyLocations.filter(agencyLocation => {
                if (agencyLocation.id === row) {
                    document.getElementById(agencyLocation.id).checked = false;
                }
                return null;
            });
            return null;
        });
        setRowsClicked([]);
    };

    const onToggle = async agencyLocation => {
        try {
            const updatedAgencyLocation = {
                ...agencyLocation,
                isActive: !agencyLocation.isActive,
            };

            await updateAgencyOffice(updatedAgencyLocation);
            Toast.success(
                `Agency Location is ${agencyLocation.isActive ? 'Disabled' : 'Enabled'}`,
            );
            loadAgencyLocations();
        } catch (error) {
            console.error(
                'Failed to toggle agency location enabled status',
                error,
            );
            Toast.error(getText('msg_72406bb8bc89'));
        }
    };

    const rowAction = async action => {
        if (rowsClicked.length > 0) {
            switch (action) {
                case 'delete': {
                    let canBeDeleted = true;
                    for (let index = 0; index < rowsClicked.length; index++) {
                        const agencyLocation = rowsClicked[index];
                        if (!(agencyLocation?.canBeDeleted ?? true)) {
                            canBeDeleted = false;
                            break;
                        }
                    }
                    if (canBeDeleted) {
                        showModal({
                            type: ModalTypes.DELETE_CONFIRMATION,
                            title: getText('msg_b957efeb6067'),
                            text: `This action will delete ${rowsClicked.length > 1 ? 'all selected agency locations' : 'this agency location'}.`,
                            onYes: () => deleteAgencyLocations(),
                            onNo: () => { },
                            noButtonText: getText('msg_77dfd2135f4d'),
                            yesButtonText: getText('msg_f6fdbe48dc54'),
                        });
                        logEvent(
                            LogEventType.DeleteClicked,
                            `Agency location delete clicked`,
                        );
                    } else {
                        showModal({
                            type: ModalTypes.ACTION,
                            title: getText('msg_b78130c92f32'),
                            subtitle: `${rowsClicked.length > 1 ? 'One or more of these agency locations are' : 'This agency location is'} currently in use`,
                            text: getText('msg_25fce019c582'),
                            onYes: () => { },
                            onNo: () => { },
                            noButtonText: getText('msg_77dfd2135f4d'),
                            yesButtonText: getText('msg_2e02623966f9'),
                        });
                    }

                    break;
                }
                default: {
                    break;
                }
            }
        } else {
            Toast.error(
                getText('msg_9bcf67dc54d5'),
            );
        }
    };

    return (
        <div className="card-body px-0 py-4">
            {isUnauthorized ? (
                <>
                    <AlertBox
                        title={getText('msg_53cc4f43d8a7')}
                        description={getText('msg_7de2a9f3cc6c')}
                    />
                </>
            ) : !showList ? (
                <LocationsForm
                    currentName={currentName}
                    currentCode={currentCode}
                    isActive={currentIsActive}
                    agencyId={agencyId}
                    locationId={currentLocationId}
                    onSave={handleSave}
                    onCancel={setShowList}
                />
            ) : (
                <>
                    {/* Screen Header with Title and Actions */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        {/* Screen Title */}
                        <p className="page-title float-start card-title ms-4"></p>
                        {/* Screen Actions */}
                        {canEditForm && (
                            <div className="float-end d-flex align-items-center">
                                <span
                                    type="button"
                                    onClick={() => rowAction('delete')}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-trash3 me-2" />
                                    {getText('msg_f6fdbe48dc54')}
                                </span>
                                <span
                                    onClick={handleAdd}
                                    type="button"
                                    className="ms-3 me-4 button-base ing-button-primary-left align-middle"
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="table-responsive-xxl w-100">
                        {/* Location List Table */}
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th className="text-center">
                                        <svg
                                            width="19"
                                            height="19"
                                            viewBox="0 0 19 19"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M5.2 9.3H12.8"
                                                stroke="#0070FF"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <rect
                                                x="0.5"
                                                y="0.5"
                                                width="17"
                                                height="17"
                                                rx="4"
                                                stroke="#0070FF"
                                            />
                                        </svg>
                                    </th>
                                    <th>
                                        <span>{getText('msg_709a23220f2c')}</span>
                                        <span
                                            onClick={handleSortName}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'name' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={() => openModal('name')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'name' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span>{getText('msg_adac69379a62')}</span>
                                        <span
                                            onClick={handleSortCode}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'code' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={() => openModal('code')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'code' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th className="text-center">
                                        <span>{getText('msg_20063ad90532')}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedAgencyLocations.map(agencyLocation => (
                                    <tr
                                        key={agencyLocation.id}
                                        className={`${rowsClicked.includes(agencyLocation.id) && agencyLocation.id != 0 ? 'clicked-class' : ''}`}
                                    >
                                        <td className="text-center">
                                            <input
                                                id={agencyLocation.id}
                                                onClick={() =>
                                                    handlerRowClicked(
                                                        agencyLocation,
                                                    )
                                                }
                                                className="form-check-input"
                                                type="checkbox"
                                            />
                                        </td>
                                        <td className="item-page-name-static">
                                            <div
                                                className="titleCellAgency"
                                                onClick={() =>
                                                    handleEdit(
                                                        agencyLocation.id,
                                                        agencyLocation.name,
                                                        agencyLocation.code,
                                                        agencyLocation.isActive,
                                                    )
                                                }
                                            >
                                                {agencyLocation.name}
                                            </div>
                                        </td>
                                        <td className="item-page-name-static">
                                            {agencyLocation.code}
                                        </td>
                                        <td className="text-center">
                                            <div className={`form-switch`}>
                                                <input
                                                    onClick={() =>
                                                        onToggle(agencyLocation)
                                                    }
                                                    className="form-check-input custom-switch-input"
                                                    type="checkbox"
                                                    checked={
                                                        agencyLocation.isActive
                                                            ? true
                                                            : false
                                                    }
                                                    disabled={!canEditForm}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p
                        className="text-center fs-5"
                        style={{
                            margin: '60px 0',
                            color: 'rgba(47, 43, 67, 0.6)',
                        }}
                    >
                        {agencyLocations.length <= 0
                            ? 'No agency locations found'
                            : ''}
                    </p>
                </>
            )}
        </div>
    );
};

export default LocationsTab;
